import { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { Loader2, Play, ChevronDown, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { useInvestigate } from '@/api/hooks/useInvestigate';
import type { ToolInvocationOut } from '@/types/alerts';

interface ToolCallTraceProps {
  tools: ToolInvocationOut[];
}

function ToolCallTrace({ tools }: ToolCallTraceProps): JSX.Element {
  const [expanded, setExpanded] = useState<Set<number>>(new Set());

  if (tools.length === 0) {
    return (
      <p className="text-xs text-muted-foreground">No tool calls recorded.</p>
    );
  }

  const toggle = (i: number) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });

  return (
    <ul className="space-y-2">
      {tools.map((t, i) => {
        const open = expanded.has(i);
        return (
          <li key={i} className="rounded-md border">
            <button
              type="button"
              onClick={() => toggle(i)}
              className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-muted/60"
            >
              {open ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )}
              <span className="font-mono text-xs font-medium">{t.tool}</span>
              {t.error && (
                <Badge variant="destructive" className="ml-auto text-xs">
                  error
                </Badge>
              )}
            </button>
            {open && (
              <div className="space-y-2 border-t bg-muted/30 px-3 py-2 text-xs">
                <div>
                  <div className="font-semibold text-muted-foreground">args</div>
                  <pre className="mt-1 overflow-x-auto rounded bg-background p-2 font-mono text-[11px]">
                    {JSON.stringify(t.args, null, 2)}
                  </pre>
                </div>
                <div>
                  <div className="font-semibold text-muted-foreground">
                    result_summary
                  </div>
                  <pre className="mt-1 whitespace-pre-wrap rounded bg-background p-2 font-mono text-[11px]">
                    {t.result_summary || '(empty)'}
                  </pre>
                </div>
                {t.error && (
                  <div>
                    <div className="font-semibold text-destructive">error</div>
                    <pre className="mt-1 whitespace-pre-wrap rounded bg-background p-2 font-mono text-[11px]">
                      {t.error}
                    </pre>
                  </div>
                )}
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
}

interface LLMInvestigationSectionProps {
  alertId: number | undefined;
}

/**
 * Layer-5 LLM investigation panel. Lifted unchanged from the previous
 * monolithic AlertDetail.tsx (same useInvestigate hook + same query key
 * `['investigation', alertId]` so any cached run survives the redesign).
 *
 * Per the user's PR 2 feedback this lives at position 2 on the page (just
 * under the header) so investigators can fire the LLM early and read its
 * narrative as the lead-in to the structured evidence sections below.
 */
export function LLMInvestigationSection({
  alertId,
}: LLMInvestigationSectionProps): JSX.Element {
  const investigate = useInvestigate(alertId);
  const investigation = investigate.data;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="text-sm">LLM Investigation</CardTitle>
          <CardDescription>
            Layer-5 narrative + tool-call trace. Latency 5–20 minutes.
          </CardDescription>
        </div>
        <Button
          onClick={() => investigate.mutate()}
          disabled={investigate.isPending || alertId == null}
        >
          {investigate.isPending ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Investigating…
            </>
          ) : (
            <>
              <Play className="mr-2 h-4 w-4" />
              Run investigation
            </>
          )}
        </Button>
      </CardHeader>
      <CardContent className="space-y-6">
        {investigate.isError && (
          <p className="text-sm text-destructive">
            Investigation failed: {investigate.error?.message}
          </p>
        )}

        {investigation && (
          <>
            <div className="prose prose-sm max-w-none dark:prose-invert">
              <ReactMarkdown>
                {investigation.narrative || '(empty narrative)'}
              </ReactMarkdown>
            </div>

            <Separator />

            <div>
              <h4 className="mb-2 text-xs font-semibold uppercase text-muted-foreground">
                Tool-call trace ({investigation.tools_used.length})
              </h4>
              <ToolCallTrace tools={investigation.tools_used} />
            </div>

            <div className="text-xs text-muted-foreground">
              Model {investigation.model_version || 'unknown'} ·{' '}
              {investigation.latency_ms} ms · {investigation.prompt_tokens}+
              {investigation.completion_tokens} tokens
              {investigation.truncated && ' · truncated'}
            </div>
          </>
        )}

        {!investigation && !investigate.isPending && !investigate.isError && (
          <p className="text-sm text-muted-foreground">
            No investigation has been run yet. Click <em>Run investigation</em>{' '}
            to ask the LLM for a Layer-5 analysis.
          </p>
        )}
      </CardContent>
    </Card>
  );
}
